import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Award, BriefcaseBusiness, Building2, Camera, Clock3, Mail, MapPin,
  Download, ExternalLink, FileText, History, ImagePlus, Pencil, Phone, RefreshCw,
  Trash2, UploadCloud, UserRound,
} from 'lucide-react';
import './Employees.css';
import {
  getDepartments,
  getDesignations,
  getEmployeeById,
  getEmployeeCompetencies,
  getEmployeeDocuments,
  updateEmployee,
} from '../hrisApi';
import EmployeeFormModal from './EmployeeFormModal';

const AVATAR_TONES = ['violet', 'blue', 'orange', 'teal', 'green', 'pink', 'purple', 'sky', 'amber', 'rose'];
const ACTIVITY_STORAGE_PREFIX = 'onealpha_hris_employee_activity_';
const LOCAL_PHOTO_PREFIX = 'onealpha_hris_employee_photo_';
const LOCAL_DOCUMENT_PREFIX = 'onealpha_hris_employee_documents_';

// Mirrors the current Master Data > Users > Document tab sample rows.
// Replace automatically when the HRIS/Datahub documents endpoint returns
// real rows for the selected employee.
const MASTER_DATA_SAMPLE_DOCUMENTS = [
  {
    id: 'md-sample-cv',
    category: 'CV',
    name: 'CV - John Doe',
    fileName: 'cv_johndoe.pdf',
    size: '2.0 MB',
    uploadedAt: '02/11/2030',
    owner: 'johndoe_101',
    status: 'Active',
    source: 'Master Data',
    local: false,
  },
  {
    id: 'md-sample-iso',
    category: 'Certificate',
    name: 'ISO 9001 Auditor Certificate',
    fileName: 'iso_9001_auditor.pdf',
    size: '2.0 MB',
    uploadedAt: '02/11/2030',
    owner: 'johndoe_101',
    status: 'Expired',
    source: 'Master Data',
    local: false,
  },
  {
    id: 'md-sample-training',
    category: 'Training',
    name: 'Environmental Management Training',
    fileName: 'env_management.pdf',
    size: '2.0 MB',
    uploadedAt: '02/11/2030',
    owner: 'johndoe_101',
    status: 'Active',
    source: 'Master Data',
    local: false,
  },
];

const initials = (name) => (name || '').split(' ').map((part) => part[0]).filter(Boolean).slice(0, 2).join('');

const photoKey = (employeeId) => `${LOCAL_PHOTO_PREFIX}${employeeId}`;

const readLocalEmployeePhoto = (employeeId) => {
  if (!employeeId) return null;
  try {
    return window.localStorage.getItem(photoKey(employeeId));
  } catch (_) {
    return null;
  }
};

const writeLocalEmployeePhoto = (employeeId, dataUrl) => {
  if (!employeeId || !dataUrl) return;
  try {
    window.localStorage.setItem(photoKey(employeeId), dataUrl);
  } catch (_) {}
};

const documentKey = (employeeId) => `${LOCAL_DOCUMENT_PREFIX}${employeeId}`;

const readLocalEmployeeDocuments = (employeeId) => {
  if (!employeeId) return [];
  try {
    const raw = window.localStorage.getItem(documentKey(employeeId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const writeLocalEmployeeDocuments = (employeeId, documents) => {
  if (!employeeId) return;
  try {
    window.localStorage.setItem(documentKey(employeeId), JSON.stringify(documents));
  } catch (_) {}
};

const resolveEmployeePhoto = (record = {}) => {
  const localPhoto = readLocalEmployeePhoto(record.id || record.employeeId);
  if (localPhoto) return localPhoto;
  const value = record.photoUrl || record.avatarUrl || record.profileImageUrl || record.imageUrl
    || record.photo || record.profilePicture || record.avatar || record.image;
  if (!value) return null;
  if (/^https?:\/\//i.test(String(value)) || String(value).startsWith('data:')) return value;
  const apiUrl = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:8080';
  return `${apiUrl}/api/public/displayimage/${encodeURIComponent(value)}`;
};

const formatJoinDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatActivityDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatFileSize = (bytes) => {
  if (typeof bytes === 'string' && bytes.trim() && Number.isNaN(Number(bytes))) return bytes;
  const size = Number(bytes || 0);
  if (!size) return '-';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDocumentDate = (value) => {
  if (!value) return '-';
  if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB');
};

const normalizeCompetencyStatus = (status) => {
  const value = String(status || '').toLowerCase();
  if (value.includes('pending')) return 'pending';
  if (value.includes('approved')) return 'approved';
  if (value.includes('rejected')) return 'rejected';
  if (value.includes('revision')) return 'need_revision';
  return value;
};

const competencyStatusLabel = (status) => {
  switch (normalizeCompetencyStatus(status)) {
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'pending':
      return 'Pending Approval';
    case 'need_revision':
      return 'Need Revision';
    default:
      return status || '-';
  }
};

const documentStatus = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return 'Submitted';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const normalizeEmployeeDocuments = (record = {}) => {
  const possibleDocuments = record.documents || record.employeeDocuments || record.files || record.attachments || [];
  const documents = Array.isArray(possibleDocuments) ? possibleDocuments : [];
  return documents.map((doc, index) => ({
    id: doc.id || doc.documentId || doc.fileId || `remote-${index}`,
    name: doc.name || doc.fileName || doc.documentName || doc.title || 'Employee document',
    fileName: doc.fileName || doc.originalFileName || doc.filename || doc.name || doc.documentName || '-',
    category: doc.category || doc.documentType || doc.type || 'Employee File',
    status: documentStatus(doc.status || doc.reviewStatus),
    owner: doc.owner || doc.uploadedBy || doc.createdBy || 'Master Data',
    version: doc.version || doc.revision || 'v1',
    uploadedAt: doc.uploadedAt || doc.createdAt || doc.updatedAt,
    expiresAt: doc.expiresAt || doc.expiryDate || doc.validUntil,
    size: doc.size || doc.fileSize,
    url: doc.url || doc.fileUrl || doc.downloadUrl || doc.path,
    source: doc.source || 'Master Data',
    local: false,
  }));
};

const normalizeDocumentResponse = (result) => {
  if (Array.isArray(result)) return normalizeEmployeeDocuments({ documents: result });
  if (Array.isArray(result?.content)) return normalizeEmployeeDocuments({ documents: result.content });
  if (Array.isArray(result?.data)) return normalizeEmployeeDocuments({ documents: result.data });
  if (Array.isArray(result?.documents)) return normalizeEmployeeDocuments({ documents: result.documents });
  return [];
};

const splitName = (record = {}) => {
  const fullName = String(record.fullName || record.name || '').trim();
  const parts = fullName.split(/\s+/).filter(Boolean);
  return {
    firstName: record.firstName || parts.slice(0, Math.max(1, parts.length - 1)).join(' ') || '',
    lastName: record.lastName || (parts.length > 1 ? parts[parts.length - 1] : ''),
  };
};

const toStatusValue = (value) => {
  if (value === 1 || value === '1' || String(value).toLowerCase() === 'active') return '1';
  if (value === 0 || value === '0' || String(value).toLowerCase() === 'inactive') return '0';
  return value ?? '';
};

const activityKey = (employeeId) => `${ACTIVITY_STORAGE_PREFIX}${employeeId}`;

const readActivityLogs = (employeeId) => {
  if (!employeeId) return [];
  try {
    const raw = window.localStorage.getItem(activityKey(employeeId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const writeActivityLogs = (employeeId, logs) => {
  if (!employeeId) return;
  try {
    window.localStorage.setItem(activityKey(employeeId), JSON.stringify(logs.slice(0, 100)));
  } catch (_) {}
};

const currentActor = () => {
  try {
    const raw = window.localStorage.getItem('user') || window.localStorage.getItem('currentUser');
    const user = raw ? JSON.parse(raw) : {};
    return user.fullName || user.name || user.email || user.username || 'Current user';
  } catch (_) {
    return 'Current user';
  }
};

const findNameById = (records, id, keys) => {
  const match = (records || []).find((item) => String(item.id) === String(id));
  if (!match) return id || '-';
  return keys.map((key) => match[key]).find(Boolean) || match.name || id || '-';
};

const fieldLabels = {
  firstName: 'First Name',
  lastName: 'Last Name',
  username: 'Username',
  email: 'Email',
  employeeId: 'Employee ID',
  nohp: 'Phone',
  departmentId: 'Department',
  jobTitleId: 'Job Title',
  businessLocation: 'Business Location',
  status: 'Status',
  address: 'Address',
  city: 'City',
  state: 'State/Province',
  postalCode: 'Postal Code',
  country: 'Country',
  emergencyContact: 'Emergency Contact',
  emergencyAddress: 'Emergency Address',
};

const activityFields = Object.keys(fieldLabels);

const rawFieldValue = (record = {}, key) => {
  const names = splitName(record);
  switch (key) {
    case 'firstName':
      return names.firstName;
    case 'lastName':
      return names.lastName;
    case 'nohp':
      return record.nohp ?? record.phone ?? '';
    case 'departmentId':
      return record.departmentId ?? record.departementId ?? record.department ?? '';
    case 'jobTitleId':
      return record.jobTitleId ?? record.designationId ?? record.designation ?? record.title ?? '';
    case 'status':
      return toStatusValue(record.status);
    default:
      return record[key] ?? '';
  }
};

const comparableValue = (value) => String(value ?? '').trim();

const displayFieldValue = (key, value, departments, jobTitles) => {
  if (value === undefined || value === null || value === '') return '-';
  if (key === 'departmentId') {
    return findNameById(departments, value, ['departmentName', 'name', 'title']);
  }
  if (key === 'jobTitleId') {
    return findNameById(jobTitles, value, ['designationName', 'jobTitleName', 'name', 'title']);
  }
  if (key === 'status') {
    return toStatusValue(value) === '1' ? 'Active' : 'Inactive';
  }
  return String(value);
};

const buildChangeLogs = (before, after, departments, jobTitles) => {
  const actor = currentActor();
  const now = new Date().toISOString();

  return activityFields.reduce((logs, key) => {
    const previous = rawFieldValue(before, key);
    const next = after[key] ?? '';
    if (comparableValue(previous) === comparableValue(next)) return logs;

    const from = displayFieldValue(key, previous, departments, jobTitles);
    const to = displayFieldValue(key, next, departments, jobTitles);
    if (comparableValue(from) === comparableValue(to)) return logs;

    logs.push({
      id: `${Date.now()}-${key}-${Math.random().toString(16).slice(2, 8)}`,
      type: 'profile-update',
      field: key,
      fieldLabel: fieldLabels[key],
      actor,
      from,
      to,
      description: `${actor} updated ${fieldLabels[key]} from ${from} to ${to}`,
      createdAt: now,
    });
    return logs;
  }, []);
};

const normalizeEmployee = (record = {}, index = 0) => ({
  routeId: String(record.id || record.employeeId || ''),
  id: record.employeeId || String(record.id || '-'),
  name: record.fullName || '-',
  email: record.email || '-',
  phone: record.phone || '-',
  title: record.designation || '-',
  department: record.department || '-',
  status: record.status || 'Active',
  address: [record.address, record.city, record.country].filter(Boolean).join(', ') || '-',
  businessLocation: record.businessLocation || '-',
  emergencyContact: record.emergencyContact || '-',
  emergencyAddress: record.emergencyAddress || '-',
  join: formatJoinDate(record.joinDate),
  competencies: [],
  projects: 0,
  availability: record.status === 'Active' ? 'Available' : 'Unavailable',
  initials: initials(record.fullName),
  photo: resolveEmployeePhoto(record),
  documents: normalizeEmployeeDocuments(record),
  tone: AVATAR_TONES[index % AVATAR_TONES.length],
  raw: record,
});

function Avatar({ employee }) {
  return (
    <span className={`employee-avatar large ${employee.tone} ${employee.photo ? 'has-photo' : ''}`}>
      <span>{employee.initials || initials(employee.name)}</span>
      {employee.photo && (
        <img
          src={employee.photo}
          alt={employee.name}
          onError={(event) => {
            event.currentTarget.style.display = 'none';
            event.currentTarget.parentElement.classList.remove('has-photo');
          }}
        />
      )}
    </span>
  );
}

function Status({ value }) {
  return <span className={`employee-status ${value.toLowerCase().replace(' ', '-')}`}><i />{value}</span>;
}

function DetailCard({ icon: Icon, label, value }) {
  return (
    <article className="employee-detail-card">
      <span><Icon size={18} /></span>
      <div>
        <small>{label}</small>
        <strong>{value || '-'}</strong>
      </div>
    </article>
  );
}

const EmployeeDetail = () => {
  const { id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState('Profile');
  const [departments, setDepartments] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [employeeDocuments, setEmployeeDocuments] = useState([]);
  const [competencyRows, setCompetencyRows] = useState([]);
  const [competencyLoading, setCompetencyLoading] = useState(false);
  const [competencySearch, setCompetencySearch] = useState('');
  const [competencyStandardFilter, setCompetencyStandardFilter] = useState('');
  const [competencyStatusFilter, setCompetencyStatusFilter] = useState('');
  const [competencyPage, setCompetencyPage] = useState(1);
  const [competencyPageSize, setCompetencyPageSize] = useState(10);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showPhotoViewModal, setShowPhotoViewModal] = useState(false);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    getEmployeeById(id)
      .then((result) => {
        const normalized = normalizeEmployee(result);
        setEmployee(normalized);
        const employeeId = normalized.raw?.id || id;
        getEmployeeDocuments(employeeId)
          .then((documentResult) => {
            const remoteDocuments = normalizeDocumentResponse(documentResult);
            const baselineDocuments = remoteDocuments.length
              ? remoteDocuments
              : (normalized.documents.length ? normalized.documents : MASTER_DATA_SAMPLE_DOCUMENTS);
            setEmployeeDocuments([
              ...baselineDocuments,
              ...readLocalEmployeeDocuments(employeeId),
            ]);
          })
          .catch(() => {
            const baselineDocuments = normalized.documents.length ? normalized.documents : MASTER_DATA_SAMPLE_DOCUMENTS;
            setEmployeeDocuments([
              ...baselineDocuments,
              ...readLocalEmployeeDocuments(employeeId),
            ]);
          });
      })
      .catch(() => setError('Could not load employee detail from the server.'))
      .finally(() => setLoading(false));
  };

  const loadOptions = () => {
    Promise.all([
      getDepartments({ page: 0, size: 500 }).catch(() => ({ content: [] })),
      getDesignations({ page: 0, size: 500 }).catch(() => ({ content: [] })),
    ]).then(([departmentResult, jobTitleResult]) => {
      setDepartments(departmentResult?.content || departmentResult?.data || []);
      setJobTitles(jobTitleResult?.content || jobTitleResult?.data || []);
    });
  };

  const loadCompetencies = (employeeId) => {
    if (!employeeId) {
      setCompetencyRows([]);
      return;
    }
    setCompetencyLoading(true);
    getEmployeeCompetencies(employeeId)
      .then((result) => setCompetencyRows(Array.isArray(result) ? result : []))
      .catch(() => setCompetencyRows([]))
      .finally(() => setCompetencyLoading(false));
  };

  const saveEmployee = async (formValues) => {
    if (!employee?.raw?.id) return;
    setSaving(true);
    setError(null);
    try {
      const changeLogs = buildChangeLogs(employee.raw, formValues, departments, jobTitles);
      await updateEmployee(employee.raw.id, formValues);
      if (changeLogs.length) {
        const nextLogs = [...changeLogs, ...readActivityLogs(id)];
        writeActivityLogs(id, nextLogs);
        setActivityLogs(nextLogs);
      }
      setShowEditModal(false);
      load();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not update employee.');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const openPhotoModal = () => {
    setPhotoPreview(employee?.photo || '');
    setPhotoError('');
    setShowPhotoModal(true);
  };

  const openPhotoViewModal = () => {
    if (!employee) return;
    setShowPhotoViewModal(true);
  };

  const handlePhotoFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setPhotoError('');
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setPhotoError('Gunakan file JPG, PNG, atau WEBP.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setPhotoError('Ukuran foto maksimal 2 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(String(reader.result || ''));
    reader.onerror = () => setPhotoError('Foto gagal dibaca. Coba pilih file lain.');
    reader.readAsDataURL(file);
  };

  const savePhoto = () => {
    if (!employee?.raw?.id || !photoPreview) {
      setPhotoError('Pilih foto terlebih dahulu.');
      return;
    }
    writeLocalEmployeePhoto(employee.raw.id, photoPreview);
    const now = new Date().toISOString();
    const actor = currentActor();
    const photoLog = {
      id: `${Date.now()}-photo-${Math.random().toString(16).slice(2, 8)}`,
      type: 'photo-update',
      field: 'photo',
      fieldLabel: 'Profile Photo',
      actor,
      from: '-',
      to: 'Updated',
      description: `${actor} updated Profile Photo`,
      createdAt: now,
    };
    const nextLogs = [photoLog, ...readActivityLogs(id)];
    writeActivityLogs(id, nextLogs);
    setActivityLogs(nextLogs);
    setEmployee((prev) => prev ? { ...prev, photo: photoPreview } : prev);
    setShowPhotoModal(false);
  };

  const logDocumentActivity = (description, fieldLabel = 'Employee Document') => {
    const now = new Date().toISOString();
    const actor = currentActor();
    const documentLog = {
      id: `${Date.now()}-document-${Math.random().toString(16).slice(2, 8)}`,
      type: 'document-update',
      field: 'documents',
      fieldLabel,
      actor,
      from: '-',
      to: 'Updated',
      description,
      createdAt: now,
    };
    const nextLogs = [documentLog, ...readActivityLogs(id)];
    writeActivityLogs(id, nextLogs);
    setActivityLogs(nextLogs);
  };

  const handleDocumentFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !employee?.raw?.id) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('Ukuran dokumen maksimal 10 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const actor = currentActor();
      const now = new Date().toISOString();
      const document = {
        id: `${Date.now()}-doc-${Math.random().toString(16).slice(2, 8)}`,
        name: file.name,
        category: 'Employee File',
        status: 'Submitted',
        owner: actor,
        version: 'v1',
        uploadedAt: now,
        expiresAt: '',
        size: file.size,
        url: String(reader.result || ''),
        source: 'Local upload',
        local: true,
      };
      const localDocuments = [document, ...readLocalEmployeeDocuments(employee.raw.id)];
      writeLocalEmployeeDocuments(employee.raw.id, localDocuments);
      setEmployeeDocuments([
        ...employeeDocuments.filter((doc) => !doc.local),
        ...localDocuments,
      ]);
      logDocumentActivity(`${actor} uploaded document ${file.name}`);
      setError(null);
    };
    reader.onerror = () => setError('Dokumen gagal dibaca. Coba pilih file lain.');
    reader.readAsDataURL(file);
  };

  const removeLocalDocument = (documentId) => {
    if (!employee?.raw?.id) return;
    const target = employeeDocuments.find((doc) => String(doc.id) === String(documentId));
    const localDocuments = readLocalEmployeeDocuments(employee.raw.id)
      .filter((doc) => String(doc.id) !== String(documentId));
    writeLocalEmployeeDocuments(employee.raw.id, localDocuments);
    setEmployeeDocuments([
      ...employeeDocuments.filter((doc) => !doc.local),
      ...localDocuments,
    ]);
    logDocumentActivity(`${currentActor()} removed document ${target?.name || documentId}`);
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => { loadOptions(); }, []);
  useEffect(() => { setActivityLogs(readActivityLogs(id)); }, [id]);
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (showPhotoModal) setShowPhotoModal(false);
      if (showPhotoViewModal) setShowPhotoViewModal(false);
      if (showEditModal) setShowEditModal(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showEditModal, showPhotoModal, showPhotoViewModal]);

  useEffect(() => {
    if (activeTab === 'Competency') {
      loadCompetencies(employee?.raw?.id || id);
    }
  }, [activeTab, employee?.raw?.id, id]);

  useEffect(() => {
    setCompetencyPage(1);
  }, [competencySearch, competencyStandardFilter, competencyStatusFilter, competencyPageSize, id]);

  const tabs = useMemo(() => ['Profile', 'Competency', 'Documents', 'Activity Log'], []);
  const competencyStandardOptions = useMemo(() => (
    Array.from(new Set(competencyRows.map((row) => row.standard).filter(Boolean)))
  ), [competencyRows]);
  const filteredCompetencyRows = useMemo(() => {
    const query = competencySearch.trim().toLowerCase();
    return competencyRows.filter((row) => {
      if (query) {
        const haystack = `${row.standard || ''} ${row.sectors || ''} ${row.qualificationName || ''} ${row.requestType || ''}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      if (competencyStandardFilter && String(row.standard) !== String(competencyStandardFilter)) {
        return false;
      }
      if (competencyStatusFilter && normalizeCompetencyStatus(row.status) !== competencyStatusFilter) {
        return false;
      }
      return true;
    });
  }, [competencyRows, competencySearch, competencyStandardFilter, competencyStatusFilter]);
  const competencyTotalPages = Math.max(1, Math.ceil(filteredCompetencyRows.length / competencyPageSize));
  const safeCompetencyPage = Math.min(competencyPage, competencyTotalPages);
  const competencyStartIndex = filteredCompetencyRows.length ? (safeCompetencyPage - 1) * competencyPageSize : 0;
  const competencyEndIndex = Math.min(competencyStartIndex + competencyPageSize, filteredCompetencyRows.length);
  const paginatedCompetencyRows = useMemo(() => (
    filteredCompetencyRows.slice(competencyStartIndex, competencyEndIndex)
  ), [filteredCompetencyRows, competencyStartIndex, competencyEndIndex]);

  useEffect(() => {
    setCompetencyPage((page) => Math.min(page, competencyTotalPages));
  }, [competencyTotalPages]);

  const renderTabContent = () => {
    if (!employee) return null;

    if (activeTab === 'Profile') {
      return (
        <div className="employee-detail-content">
          <section>
            <h2>Employment Information</h2>
            <div className="detail-grid detail-grid-wide">
              <div><span>Employee ID</span><strong>{employee.id}</strong></div>
              <div><span>Full Name</span><strong>{employee.name}</strong></div>
              <div><span>Department</span><strong>{employee.department}</strong></div>
              <div><span>Job Title</span><strong>{employee.title}</strong></div>
              <div><span>Status</span><strong>{employee.status}</strong></div>
              <div><span>Availability</span><strong>{employee.availability}</strong></div>
            </div>
          </section>

          <section>
            <div className="drawer-title">
              <h2>Contact</h2>
              <span>Master Data profile</span>
            </div>
            <div className="detail-grid">
              <div><span>Email</span><strong>{employee.email}</strong></div>
              <div><span>Phone</span><strong>{employee.phone}</strong></div>
              <div><span>Business Location</span><strong>{employee.businessLocation}</strong></div>
              <div><span>Emergency Contact</span><strong>{employee.emergencyContact}</strong></div>
              <div className="wide"><span>Location</span><strong>{employee.address}</strong></div>
              <div className="wide"><span>Emergency Address</span><strong>{employee.emergencyAddress}</strong></div>
            </div>
          </section>
        </div>
      );
    }

    if (activeTab === 'Competency') {
      return (
        <div className="employee-detail-single">
          <div className="drawer-title">
            <h2>Competencies</h2>
            <span>{filteredCompetencyRows.length} of {competencyRows.length} records</span>
          </div>
          <div className="employee-competency-toolbar">
            <input
              className="employee-competency-search"
              placeholder="Search standard, qualification, scheme..."
              value={competencySearch}
              onChange={(event) => setCompetencySearch(event.target.value)}
            />
            <select
              value={competencyStandardFilter}
              onChange={(event) => setCompetencyStandardFilter(event.target.value)}
            >
              <option value="">All standards</option>
              {competencyStandardOptions.map((standard) => (
                <option key={standard} value={standard}>{standard}</option>
              ))}
            </select>
            <select
              value={competencyStatusFilter}
              onChange={(event) => setCompetencyStatusFilter(event.target.value)}
            >
              <option value="">All status</option>
              <option value="pending">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="need_revision">Need Revision</option>
            </select>
            <button
              type="button"
              className="employee-competency-clear"
              onClick={() => {
                setCompetencySearch('');
                setCompetencyStandardFilter('');
                setCompetencyStatusFilter('');
              }}
            >
              Clear
            </button>
          </div>
          {competencyLoading ? (
            <div className="employee-empty-panel">
              <Award size={22} />
              <strong>Loading competency records...</strong>
              <small>Taking the latest competency requests from Master Data.</small>
            </div>
          ) : filteredCompetencyRows.length ? (
            <div className="employee-competency-table-wrap">
              <table className="employee-competency-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Standard</th>
                    <th>Qualification</th>
                    <th>Sector/Scheme</th>
                    <th>Request Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCompetencyRows.map((row) => {
                    const isUpgrade = row.requestType === 'Upgrade';
                    const status = normalizeCompetencyStatus(row.status);
                    return (
                      <tr key={row.id}>
                        <td>
                          <span className={`employee-competency-type ${isUpgrade ? 'upgrade' : 'new'}`}>
                            {isUpgrade ? 'Upgrade' : 'New'}
                          </span>
                        </td>
                        <td><strong>{row.standard || '-'}</strong></td>
                        <td>
                          {isUpgrade && row.currentTypeName && row.requestedTypeName ? (
                            <div className="employee-competency-flow">
                              <span>{row.currentTypeName}</span>
                              <small>to</small>
                              <span>{row.requestedTypeName}</span>
                            </div>
                          ) : (
                            <span className="employee-competency-pill">{row.qualificationName || 'N/A'}</span>
                          )}
                        </td>
                        <td><span className="employee-competency-muted">{row.sectors || '-'}</span></td>
                        <td>{formatDate(row.requestDate)}</td>
                        <td>
                          <span className={`employee-competency-status ${status}`}>
                            {competencyStatusLabel(row.status)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="employee-competency-footer">
                <span>
                  Showing {filteredCompetencyRows.length ? competencyStartIndex + 1 : 0}-{competencyEndIndex} of {filteredCompetencyRows.length} records
                </span>
                <div className="employee-competency-pagination">
                  <label>
                    Rows per page
                    <select
                      value={competencyPageSize}
                      onChange={(event) => setCompetencyPageSize(Number(event.target.value))}
                    >
                      {[10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </label>
                  <div className="employee-competency-pager">
                    <button
                      type="button"
                      onClick={() => setCompetencyPage((page) => Math.max(1, page - 1))}
                      disabled={safeCompetencyPage <= 1}
                    >
                      Prev
                    </button>
                    <strong>{safeCompetencyPage}</strong>
                    <button
                      type="button"
                      onClick={() => setCompetencyPage((page) => Math.min(competencyTotalPages, page + 1))}
                      disabled={safeCompetencyPage >= competencyTotalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="employee-empty-panel">
              <Award size={22} />
              <strong>No competency requests found.</strong>
              <small>Data follows Master Data competency requests for this employee.</small>
            </div>
          )}
        </div>
      );
    }

    if (activeTab === 'Documents') {
      return (
        <div className="employee-detail-single">
          <div className="drawer-title">
            <h2>Documents</h2>
            <span>{employeeDocuments.length} files</span>
          </div>
          <div className="employee-document-toolbar">
            <div>
              <strong>Employee Document Register</strong>
              <small>Employment contract, ID document, certificates, and HR supporting files.</small>
            </div>
            <label className="employee-document-upload">
              <UploadCloud size={16} /> Upload Document
              <input type="file" onChange={handleDocumentFile} />
            </label>
          </div>
          {employeeDocuments.length ? (
            <div className="employee-document-table-wrap">
              <table className="employee-document-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Document Name</th>
                    <th>File Name</th>
                    <th>Size</th>
                    <th>Upload Date</th>
                    <th>Upload By</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeDocuments.map((doc) => (
                    <tr key={doc.id}>
                      <td><span className="employee-document-category">{doc.category}</span></td>
                      <td>
                        <div className="employee-document-name">
                          <FileText size={17} />
                          <div>
                            <strong>{doc.name}</strong>
                            <small>{doc.source}</small>
                          </div>
                        </div>
                      </td>
                      <td><span className="employee-document-file">{doc.fileName || '-'}</span></td>
                      <td>{formatFileSize(doc.size)}</td>
                      <td>{formatDocumentDate(doc.uploadedAt)}</td>
                      <td>{doc.owner || '-'}</td>
                      <td><span className={`employee-document-status ${doc.status.toLowerCase().replace(/\s+/g, '-')}`}>{doc.status}</span></td>
                      <td>
                        <div className="employee-document-actions">
                          {doc.url && (
                            <a href={doc.url} target="_blank" rel="noopener noreferrer" aria-label={`Open ${doc.name}`}>
                              <ExternalLink size={16} />
                            </a>
                          )}
                          {doc.local && (
                            <button type="button" onClick={() => removeLocalDocument(doc.id)} aria-label={`Remove ${doc.name}`}>
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="employee-empty-panel">
              <FileText size={22} />
              <strong>No employee document uploaded.</strong>
              <small>Upload employee files here while waiting for permanent HRIS document storage from the backend.</small>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="employee-detail-single">
        <div className="drawer-title">
          <h2>Activity Log</h2>
          <span>{activityLogs.length} updates</span>
        </div>
        {activityLogs.length ? (
          <div className="employee-activity-list">
            {activityLogs.map((log) => (
              <article className="employee-activity-item" key={log.id}>
                <span className="employee-activity-icon"><History size={16} /></span>
                <div>
                  <strong>{log.fieldLabel || 'Employee profile updated'}</strong>
                  <p>{log.description}</p>
                  <small>{formatActivityDate(log.createdAt)}</small>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="employee-empty-panel">
            <History size={22} />
            <strong>No activity log yet.</strong>
            <small>Profile changes from this browser will appear here after employee data is updated.</small>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="employee-directory-page employee-detail-page">
      <div className="employee-detail-topbar">
        <Link className="back-link" to="/hris/employees"><ArrowLeft size={18} /> Back to Employees</Link>
        <div className="employee-detail-actions">
          {employee && (
            <button className="refresh-button" onClick={() => setShowEditModal(true)} disabled={saving}>
              <Pencil size={17} /> Edit Employee
            </button>
          )}
          <button className="refresh-button" onClick={load} disabled={loading}><RefreshCw size={17} /> Refresh</button>
        </div>
      </div>

      <header className="employee-detail-hero">
        {employee ? (
          <>
            <div className="employee-photo-block">
              <button type="button" className="employee-photo-view-trigger" onClick={openPhotoViewModal} aria-label="View employee photo">
                <Avatar employee={employee} />
              </button>
              <button type="button" className="employee-photo-change" onClick={openPhotoModal}>
                <Camera size={14} /> Change Photo
              </button>
            </div>
            <div>
              <div className="eyebrow"><UserRound size={15} /> EMPLOYEE PROFILE</div>
              <h1>{employee.name}</h1>
              <p>{employee.id} · {employee.title}</p>
              <div className="employee-hero-meta">
                <Status value={employee.status} />
                <span>{employee.department}</span>
              </div>
            </div>
          </>
        ) : (
          <div>
            <div className="eyebrow"><UserRound size={15} /> EMPLOYEE PROFILE</div>
            <h1>{loading ? 'Loading employee...' : 'Employee not available'}</h1>
            <p>{error || 'Employee detail will appear here.'}</p>
          </div>
        )}
      </header>

      {error && <div className="employee-detail-alert">{error}</div>}

      {employee && (
        <>
          <section className="employee-detail-grid">
            <DetailCard icon={Mail} label="Email" value={employee.email} />
            <DetailCard icon={Phone} label="Phone" value={employee.phone} />
            <DetailCard icon={Building2} label="Department" value={employee.department} />
            <DetailCard icon={BriefcaseBusiness} label="Job title" value={employee.title} />
            <DetailCard icon={Clock3} label="Join date" value={employee.join} />
            <DetailCard icon={MapPin} label="Location" value={employee.address} />
          </section>

          <section className="employee-detail-panel">
            <nav className="employee-detail-tabs">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={activeTab === tab ? 'selected' : ''}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </nav>
            {renderTabContent()}
          </section>
        </>
      )}

      <EmployeeFormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={saveEmployee}
        employee={employee?.raw || null}
        departments={departments}
        jobTitles={jobTitles}
        saving={saving}
      />

      {showPhotoViewModal && employee && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
          <div className="modal fade show employee-photo-modal employee-photo-view-modal" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Employee Photo</h5>
                  <button type="button" className="btn-close" onClick={() => setShowPhotoViewModal(false)} aria-label="Close" />
                </div>
                <div className="modal-body">
                  <div className="employee-photo-view-frame">
                    {employee.photo ? (
                      <img src={employee.photo} alt={`${employee.name} profile`} />
                    ) : (
                      <div className="employee-photo-empty">
                        <span>{employee.initials || initials(employee.name)}</span>
                        <strong>No employee photo yet</strong>
                        <small>Upload a profile photo for easier employee identification.</small>
                      </div>
                    )}
                  </div>
                  <div className="employee-photo-view-meta">
                    <strong>{employee.name}</strong>
                    <span>{employee.id} · {employee.title || '-'}</span>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-link link-secondary" onClick={() => setShowPhotoViewModal(false)}>Close</button>
                  {employee.photo && (
                    <a className="btn btn-outline-primary" href={employee.photo} download={`${employee.id || 'employee'}-photo`}>
                      <Download size={16} /> Download
                    </a>
                  )}
                  <button type="button" className="btn btn-primary" onClick={() => { setShowPhotoViewModal(false); openPhotoModal(); }}>
                    <Camera size={16} /> Change Photo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showPhotoModal && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
          <div className="modal fade show employee-photo-modal" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Update Employee Photo</h5>
                  <button type="button" className="btn-close" onClick={() => setShowPhotoModal(false)} aria-label="Close" />
                </div>
                <div className="modal-body">
                  {photoError && <div className="alert alert-danger py-2">{photoError}</div>}
                  <div className="employee-photo-preview">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Employee preview" />
                    ) : (
                      <span>{employee.initials || initials(employee.name)}</span>
                    )}
                  </div>
                  <label className="employee-photo-dropzone">
                    <ImagePlus size={22} />
                    <strong>Select employee photo</strong>
                    <small>JPG, PNG, or WEBP. Maximum 2 MB.</small>
                    <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handlePhotoFile} />
                  </label>
                  <p className="employee-photo-note">
                    Foto ini digunakan untuk identifikasi employee di HRIS. Upload ke backend permanen perlu endpoint HRIS photo di Go API.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-link link-secondary" onClick={() => setShowPhotoModal(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={savePhoto} disabled={!photoPreview}>Save Photo</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EmployeeDetail;
